<template>
  <form-section
    :title="$t('CampaignForm_Events_SelectEvent_Title')"
    :description="$t('CampaignForm_Events_SelectEvent_Desc')"
    :separator="true"
    :is-changed="model.diff && model.diff.includes('event')"
  >
    <form-item v-if="eventType">
      <radio-group inline v-model="eventType">
        <radio
          :border="true"
          label="custom"
          :title="$t('CampaignForm_Events_Event_Custom')"
        />
        <radio
          border
          label="system"
          :title="$t('CampaignForm_Events_Event_System_Event')"
        />
      </radio-group>
    </form-item>

    <div v-if="eventType === 'custom'">
      <el-row>
        <el-col :span="8">
          <form-item :label="$t('CampaignForm_Events_Event')">
            <el-autocomplete
              :placeholder="$t('CampaignForm_Select_Event')"
              clearable
              v-model="model.event_title"
              :fetch-suggestions="querySearchEvents"
              :select-when-unmatched="true"
              :popper-options="{
                modifiers: [
                  hidePopover,
                  'offset',
                  'keepTogether',
                  'arrow',
                  'applyStyle'
                ]
              }"
              style="width: 100%"
              @select="handleSelectEvents"
              @blur="onBlur"
              @clear="clearEventsSelection"
            />
          </form-item>
        </el-col>
        <el-col :span="8" style="padding-top: 40px">
          <toggle
            v-model="model.event_filter"
            :text="$t('CampaignForm_Select_Event_Filter')"
          />
        </el-col>
      </el-row>

      <conditions-editor
        ref="conditions-editor"
        v-if="model.event_filter"
        v-model="model.event_conditions"
        data-model-name="Campaign"
        data-model-attribute="event_conditions"
        attribute-list-data-url="/push/event-filter/attribute-list-data"
        :preview="false"
        :no-data-alert="false"
        :single-level="true"
      />
    </div>

    <div v-if="eventType === 'system'">
      <el-row>
        <el-col :span="12">
          <form-item
            :label="$t('CampaignForm_Events_Event')"
            :hint="consentHint"
          >
            <el-select
              ref="systemEventSelect"
              v-model="model.event"
              filterable
              clearable
              :placeholder="$t('CampaignForm_Select_Event')"
            >
              <el-option
                v-if="
                  isConsentPreferencesEnabled || isSubscriptionChangeSelected
                "
                :key="1"
                :label="
                  $t(
                    campaignSystemEventTitles[
                      campaignSystemEvents.EVENT_PREFERENCE_TRIGGER
                    ]
                  )
                "
                :value="campaignSystemEvents.EVENT_PREFERENCE_TRIGGER"
              />

              <el-option
                :key="2"
                :label="
                  $t(
                    campaignSystemEventTitles[
                      campaignSystemEvents.EVENT_SESSION_START
                    ]
                  )
                "
                :value="campaignSystemEvents.EVENT_SESSION_START"
              />
            </el-select>
          </form-item>
        </el-col>

        <el-col
          :span="8"
          class="event-filter-toggles"
          style="padding-top: 40px"
          v-if="isSubscriptionChangeSelected"
        >
          <toggle
            v-model="model.event_filter"
            :text="$t('CampaignForm_Select_Event_Filter_System_Event')"
          />
          <el-tooltip
            v-if="model.event_filter"
            class="item"
            effect="light"
            placement="top"
            popper-class="white-tooltip"
          >
            <div slot="content">
              {{ $t('CampaignForm_System_Event_Full_Payload_Tooltip') }}
            </div>
            <toggle
              v-model="model.system_event_full_payload"
              :text="$t('CampaignForm_System_Event_Full_Payload_Title')"
            />
          </el-tooltip>
        </el-col>
        <el-col>
          <system-consent-preference-setup
            v-if="isSubscriptionChangeSelected && !model.event_filter"
            v-model="model"
          ></system-consent-preference-setup>
        </el-col>
      </el-row>

      <conditions-editor
        ref="conditions-editor"
        v-if="isSubscriptionChangeSelected && model.event_filter"
        v-model="model.event_conditions"
        data-model-name="Campaign"
        data-model-attribute="event_conditions"
        attribute-list-data-url="/push/event-filter/system-event-attributes"
        :preview="false"
        :no-data-alert="false"
        :single-level="true"
      />
    </div>
  </form-section>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import {
  campaignSystemEvents,
  campaignSystemEventTitles
} from '@/modules/campaigns/enum/CampaignSystemEventEnum'
import FormItem from '@/common/components/form/FormItem'
import ConditionsEditor from '@/common/components/ConditionsEditor'
import Toggle from '@/common/components/form/Toggle'
import { $hasFeature } from '@/core/utils/VueAcl'
import SystemConsentPreferenceSetup from '@/modules/campaign-manage/components/setup/SystemConsentPreferenceSetup'

export default {
  name: 'EventSelectorExtended',

  components: {
    Toggle,
    ConditionsEditor,
    FormItem,
    Radio,
    RadioGroup,
    FormSection,
    SystemConsentPreferenceSetup
  },

  props: {
    model: {
      type: Object
    }
  },

  data() {
    return {
      campaignSystemEvents,
      campaignSystemEventTitles,
      eventType: null
    }
  },

  mounted() {
    this.initEventType()
    this.initSystemEventFullPayload()
  },

  computed: {
    isConsentPreferencesEnabled() {
      return (
        $hasFeature('subscriptionPreferences') &&
        $hasFeature('campaignTriggerByConsentPrefEvents')
      )
    },

    isSubscriptionChangeSelected() {
      return this.model.event === campaignSystemEvents.EVENT_PREFERENCE_TRIGGER
    },

    consentHint() {
      if (this.isSubscriptionChangeSelected) {
        return this.$t('CampaignForm_Events_Event_Consent_Preference_Hint')
      }

      return ''
    }
  },

  methods: {
    querySearchEvents(queryString, cb) {
      const params = {
        sort: 'title'
      }

      if (queryString !== null && queryString !== '') {
        params.title = queryString
      }

      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'events',
          params: params
        })
      ).then(({ data }) => {
        const list = data.map(event => {
          return {
            value: event.title,
            type: event.type
          }
        })
        cb(list)
      })
    },

    onBlur() {
      this.handleSelectEvents({ type: 'custom' })
    },

    handleSelectEvents(selection) {
      this.model.event = selection.type ? selection.type : 'custom'
    },

    clearEventsSelection() {
      this.model.event = null
      this.model.event_title = null
    },

    hidePopover(data) {
      const refRect = data.offsets.reference
      const headerHeight =
        document.querySelector('header > .top-container').offsetHeight || 64

      data.styles.display = refRect.bottom - headerHeight < 0 ? 'none' : 'block'

      return data
    },

    initEventType() {
      switch (this.model.event) {
        case campaignSystemEvents.EVENT_PREFERENCE_TRIGGER:
        case campaignSystemEvents.EVENT_SESSION_START:
          this.eventType = 'system'
          break
        default:
          this.eventType = 'custom'
      }
    },
    initSystemEventFullPayload() {
      if (
        this.model.system_event_full_payload === undefined ||
        this.model.system_event_full_payload === null
      ) {
        this.$set(this.model, 'system_event_full_payload', 1)
      }
    }
  },

  watch: {
    eventType: {
      handler(val, oldVal) {
        if (val === 'custom') {
          this.model.event = 'custom'
        } else if (oldVal !== null) {
          this.model.event = null
        }
      }
    },
    'model.event': {
      handler() {
        this.model.event_title = null
      }
    },
    'model.event_filter'(val) {
      if (!val) {
        this.model.system_event_full_payload = 1
      }
    }
  }
}
</script>

<style>
.white-tooltip {
  background-color: white !important;
  border: 1px solid #409eff !important;
  color: #606266 !important;
}
.event-filter-toggles {
  display: flex;
  gap: 10px;
}
@media screen and (max-width: 1467px) {
  .event-filter-toggles {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/events/EventSelectorExtended.vue